import React, { useState } from 'react';

import { Button, Card, Form } from 'react-bootstrap';

import {
  DATA_REPORTS_MFR_PRODUCT_GMV,
  GLOBAL_MANUFACTURERS_FOR_DROPDOWN,
} from 'lib/networking/endpoints';
import { ManufacturerVerificationStatus } from 'lib/enums/ManufacturerVerificationStatus';
import MfrProductGmvReportModal from 'components/data_reports/MfrProductGmvReportModal';
import SearchableScrollableMultiSelect from 'components/shared/SearchableScrollableMultiSelect';
import SwalDialog, { exportFileSuccessSwal } from 'lib/utils/toast';
import usePost from 'lib/hooks/usePost';

const MAX_MANUFACTURERS = 10;
const DEFAULT_RANGE_DAYS = 30;
const VERIFIED_MANUFACTURERS_PARAMS = {
  verification_status: ManufacturerVerificationStatus.VERIFIED,
};

const toIsoDate = date => {
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${date.getFullYear()}-${month}-${day}`;
};

// Display an ISO yyyy-mm-dd string as MM/DD/YY without timezone shifts.
const formatMmDdYy = isoDate => {
  const [year, month, day] = isoDate.split('-');
  return `${month}/${day}/${year.slice(2)}`;
};

const defaultFromDate = () => {
  const date = new Date();
  date.setDate(date.getDate() - DEFAULT_RANGE_DAYS);
  return toIsoDate(date);
};

function MfrProductGmvReportCard() {
  const today = toIsoDate(new Date());
  const [manufacturers, setManufacturers] = useState([]);
  const [from, setFrom] = useState(defaultFromDate());
  const [to, setTo] = useState(today);
  const [showModal, setShowModal] = useState(false);
  const [limitHint, setLimitHint] = useState(false);

  const { postData: generateReport, loading: submitting } = usePost(
    DATA_REPORTS_MFR_PRODUCT_GMV,
    response => {
      setShowModal(false);
      exportFileSuccessSwal(
        response?.data?.message || 'The report files will be generated in a while.',
      );
    },
    error => {
      setShowModal(false);
      const message =
        error?.response?.data?.message || 'An error occurred while generating the report';
      SwalDialog('error', message, 'Error', 'center');
    },
  );

  const datesValid = Boolean(from) && Boolean(to) && from <= to && to <= today;
  const canGenerate = manufacturers.length > 0 && datesValid;

  const handleManufacturersChange = options => {
    const next = options || [];
    if (next.length > MAX_MANUFACTURERS) {
      // New array reference makes the select reset to the previous selection.
      setManufacturers([...manufacturers]);
      setLimitHint(true);
      return;
    }
    setLimitHint(false);
    setManufacturers(next);
  };

  const handleSubmit = reportTypes => {
    if (submitting) {
      return;
    }
    generateReport({
      manufacturer_ids: manufacturers.map(option => option.value),
      from_date: from,
      to_date: to,
      report_types: reportTypes,
    });
  };

  return (
    <Card className="shadow-sm">
      <Card.Body>
        <Card.Title className="mb-3 font-weight-bold">Manufacturer Product GMV Report</Card.Title>
        <Card.Text className="text-muted">
          Manufacturer products with GMV from DP-partner, Instacart-ads-enabled distributors over
          the chosen date range. One file is generated per manufacturer and report type.
        </Card.Text>
        <Form>
          <Form.Group>
            <Form.Label>Manufacturers (up to {MAX_MANUFACTURERS})</Form.Label>
            <SearchableScrollableMultiSelect
              onChange={handleManufacturersChange}
              fetchUrl={GLOBAL_MANUFACTURERS_FOR_DROPDOWN}
              optionMapper={manufacturer => ({ label: manufacturer.name, value: manufacturer.id })}
              initOptions={manufacturers}
              additionalParams={VERIFIED_MANUFACTURERS_PARAMS}
            />
            {limitHint && (
              <Form.Text className="text-danger">
                You can select at most {MAX_MANUFACTURERS} manufacturers.
              </Form.Text>
            )}
          </Form.Group>
          <Form.Group controlId="mfrGmvFromDate">
            <Form.Label>From</Form.Label>
            <Form.Control
              type="date"
              value={from}
              max={to || today}
              onChange={e => setFrom(e.target.value)}
              isInvalid={Boolean(from) && Boolean(to) && from > to}
            />
            {from && <Form.Text className="text-muted">{formatMmDdYy(from)}</Form.Text>}
          </Form.Group>
          <Form.Group controlId="mfrGmvToDate">
            <Form.Label>To</Form.Label>
            <Form.Control
              type="date"
              value={to}
              max={today}
              onChange={e => setTo(e.target.value)}
              isInvalid={Boolean(to) && to > today}
            />
            {to && <Form.Text className="text-muted">{formatMmDdYy(to)}</Form.Text>}
          </Form.Group>
          <Button
            variant="primary"
            className="w-100"
            disabled={!canGenerate}
            onClick={() => setShowModal(true)}
          >
            Generate
          </Button>
        </Form>
      </Card.Body>
      <MfrProductGmvReportModal
        show={showModal}
        onHide={() => setShowModal(false)}
        onSubmit={handleSubmit}
        submitting={submitting}
      />
    </Card>
  );
}

export default MfrProductGmvReportCard;
